import React, { createContext, useContext } from 'react';

/**
 * PortalSectionContext
 *
 * Lets a portal page (e.g. ClientPortal) declare itself as the owner of a
 * single editorial hero, so any nested `PortalHeader`s inside its tabs render
 * in a compact "section" mode instead of repeating the same workspace
 * eyebrow + oversized headline that already appears in the hero above.
 *
 * Components that don't live inside a provider keep the legacy default,
 * so attorney/in-house/admin/marketing pages are unaffected.
 */
export interface PortalSectionContextValue {
  /**
   * When true, nested PortalHeaders hide the workspace eyebrow and shrink
   * the title so the hierarchy reads as: hero → section → content.
   */
  compact: boolean;
}

const PortalSectionContext = createContext<PortalSectionContextValue>({
  compact: false,
});

export const PortalSectionProvider: React.FC<{
  compact?: boolean;
  children: React.ReactNode;
}> = ({ compact = true, children }) => (
  <PortalSectionContext.Provider value={{ compact }}>
    {children}
  </PortalSectionContext.Provider>
);

export function usePortalSection(): PortalSectionContextValue {
  return useContext(PortalSectionContext);
}
